import moment from '@/common/modules/moment-fallback';
import type {
    SearchResult,
    Accommodation as Tommy3Accommodation,
    MetaAgeCategory as Tommy3MetaAgeCategory,
    Map as Tommy3Map,
    TranslatedField,
    Location as Tommy3Location,
    Item as Tommy3Item,
    PriceCalculation as Tommy3PriceCalculation,
    Offer as Tommy3Offer,
    MadeBooking as Tommy3MadeBooking,
    Booking as Tommy3Booking,
    Transaction as Tommy3Transaction,
    Payment as Tommy3Payment,
} from '@/widgets/Wizard/models/tommy3';
import type {
    Accommodation,
    AgeCategory,
    AvailableAccommodation,
    MetaData,
    Map,
    TranslatedValue,
    AccommodationLocation,
    AccommodationExtra,
    PriceCalculation,
    MadeBooking,
    Transaction,
    Payment,
    ExistingBooking,
} from '@/widgets/Wizard/models/wizard';

export function parseMetaData(metaData: any): MetaData {
    const accommodations: Accommodation[] = [];
    const ageCategories: AgeCategory[] = [];
    let map: Map | null = null;
    let customerFields = [];

    // Parse accommodations
    if (Array.isArray(metaData.accommodations)) {
        const metaDataAccommodations: Tommy3Accommodation[] = metaData.accommodations;

        for (const accommodation of metaDataAccommodations) {
            // Disabled since it messes up camperpark amsterdam...
            // if (!accommodation['show-location-online']) {
            //     continue;
            // }

            accommodations.push({
                id: accommodation.id,
                title: parseTranslatedValues(accommodation.name),
                checkInTime: accommodation['check-in-time'] ?? '12:30',
                checkOutTime: accommodation['check-out-time'] ?? '12:00',
                images: accommodation.images.map((image) => image.original),
            });
        }
    }

    // Parse age categories
    if (Array.isArray(metaData['age-categories'])) {
        const metaDataAgeCategories: Tommy3MetaAgeCategory[] = metaData['age-categories'];

        for (const ageCategory of metaDataAgeCategories) {
            let suffix = null;

            if (ageCategory.from > 0 && ageCategory.till > 0) {
                suffix = `(${ageCategory.from} - ${ageCategory.till})`;
            }

            ageCategories.push({
                id: ageCategory.id,
                name: parseTranslatedValues(ageCategory.name),
                suffix,
                defaultAmount: ageCategory['default-pax'] ?? 0,
            });
        }
    }

    // Parse map
    if (metaData['reservation-settings'] && metaData['reservation-settings'].map) {
        const tommyMap: Tommy3Map = metaData['reservation-settings'].map;

        map = {
            src: tommyMap.src,
            width: tommyMap.width,
            height: tommyMap.height,
        };
    }

    // Parse customer fields
    if (metaData['customer-field-settings'] && Array.isArray(metaData['customer-field-settings'])) {
        customerFields = metaData['customer-field-settings'];
    }

    // Parse currency
    let currency = 'EUR';

    if (metaData.accounts && Array.isArray(metaData.accounts) && metaData.accounts.length > 0) {
        const account = metaData.accounts[0];

        if (account.currency) {
            const accountCurrency = parseCurrency(account.currency);

            if (accountCurrency.length > 0) {
                currency = accountCurrency;
            }
        }
    }

    const formattedMetaData: MetaData = {
        accommodations,
        ageCategories,
        map,
        customerFieldSettings: customerFields,
        currency,
    };

    return formattedMetaData;
}

export function parseCountriesResults(
    countries: { code: string; name: string; flag: string }[],
): { code: string; name: string }[] {
    return countries.map((country) => ({
        code: country.code,
        name: country.name,
    }));
}

export function parseSearchResults(searchResults: SearchResult[]): AvailableAccommodation[] {
    return searchResults.map((searchResult) => ({
        id: searchResult.id,
        prices: searchResult.periods.map((period) => {
            return {
                arrival: moment(period['date-from']).getDateObject(),
                departure: moment(period['date-till']).getDateObject(),
                price: {
                    cents: period.price.total * 100,
                    currency: parseCurrency(searchResult.currency),
                },
            };
        }),
    }));
}

export function parseTranslatedValues(fields: TranslatedField[]): TranslatedValue {
    const translated: TranslatedValue = {};

    for (const field of fields) {
        translated[field.language] = field.value;
    }

    return translated;
}

export function parseCurrency(currency: string): string {
    switch (currency) {
        case 'euro':
            return 'EUR';
    }

    console.warn(`Currency "${currency}" is not supported`);

    return '';
}

export function parseAccommodationLocations(data: Tommy3Location[]): AccommodationLocation[] {
    const parsed: AccommodationLocation[] = [];

    for (const location of data) {
        if (!location.active) {
            continue;
        }

        parsed.push({
            id: location.id,
            title: parseTranslatedValues(location.name),
            accommodation: {
                id: location.accommodationId,
                title: parseTranslatedValues(location.accommodationName),
            },
            map: {
                x: location.x,
                y: location.y ? 1 - location.y : null,
            },
        });
    }

    return parsed;
}

export function parseOffer(data: Tommy3Offer): AccommodationLocation[] {
    const parsed: AccommodationLocation[] = [];

    for (const location of data.locations ?? []) {
        if (!location.map || !location.available) {
            continue;
        }

        parsed.push({
            id: location.id,
            title: parseTranslatedValues(location.name),
            map: {
                x: location.map.x,
                y: location.map.y,
            },
        });
    }

    return parsed;
}

export function parseItemsResult(data: Tommy3Item[]): AccommodationExtra[] {
    return data
        .sort((itemA, itemB) => {
            return itemA.order - itemB.order;
        })
        .map((item) => {
            return {
                id: item.id,
                title: parseTranslatedValues(item.name),
                description: parseTranslatedValues(item.description),
                required: item.mandatory,
                max: item['maximum-amount'] && item['maximum-amount'] > 0 ? item['maximum-amount'] : null,
            };
        });
}

export function parsePriceCalculation(data: Tommy3PriceCalculation): PriceCalculation {
    const currency = parseCurrency(data.currency);

    return {
        costOfStay: {
            cents: data.costsOfStay * 100,
            currency,
        },
        extras: data.items.calculations.map((item) => {
            return {
                id: item.id,
                title: item.name,
                amount: item.amount,
                price: {
                    cents: item.price * 100,
                    currency,
                },
            };
        }),
        arrangements: data.arrangements
            ? {
                  calculations: data.arrangements.calculations.map((arrangement) => {
                      return {
                          id: arrangement.id,
                          title: arrangement.name,
                          price: {
                              cents: arrangement.price * 100,
                              currency,
                          },
                      };
                  }),
                  total: {
                      cents: data.arrangements.total * 100,
                      currency,
                  },
              }
            : undefined,
        total: {
            cents: data.total * 100,
            currency,
        },
    };
}

export function parseNewBooking(data: Tommy3MadeBooking): MadeBooking {
    return {
        id: data.id,
        reservationNumber: data['reservation-number'],
        paymentUrl: data['payment-url'] ?? null,
    };
}

export function parseBooking(data: Tommy3Booking): ExistingBooking {
    const firstLocation = data.locations[0];

    const accommodation = firstLocation
        ? {
              id: firstLocation.accommodation.id,
              title: parseTranslatedValues(firstLocation.accommodation.name),
          }
        : null;

    const location = firstLocation
        ? {
              id: firstLocation.id,
              title: parseTranslatedValues(firstLocation.name),
          }
        : null;

    return {
        id: data.id,
        reservationNumber: data.reservationNumber ?? '',
        paymentUrl: null,
        paid: data.amountOpen === 0,
        totalAmount: data.amountTotal,
        openAmount: data.amountOpen,
        paidAmount: data.amountPaid,
        accommodation,
        location,
        arrivalDate: moment(data.arrivalDate, 'YYYY-MM-DD').getDateObject(),
        departureDate: moment(data.departureDate, 'YYYY-MM-DD').getDateObject(),
        arrived: data.arrived,
        departed: data.departed,
    };
}

export function parseTransaction(data: Tommy3Transaction): Transaction {
    return {
        id: data.id,
        paid: data.paymentStatus === 'done' && data.debit === 0,
        debit: data.debit,
        credit: data.credit,
    };
}

export function parsePayment(data: Tommy3Payment): Payment {
    return {
        paid: data.paid,
        totalAmount: data.totalAmount,
        openAmount: data.openAmount,
        paidAmount: data.paidAmount,
    };
}
